import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Trash2 } from 'lucide-react';

export function CoefRow({
  item,
  onChange,
  fields = ['coef'],
  extra = null,
  testidPrefix,
  editableLabel = false,
  editableKey = false,
  rangeFields = null, // ex: ['min_years', 'max_years']
  onDelete = null,
}) {
  // Layout dynamique : on calcule la largeur de la colonne label en fonction
  // des champs additionnels (range + coef/pct + delete).
  const hasRange = Array.isArray(rangeFields) && rangeFields.length > 0;
  // Mapping explicite (Tailwind JIT n'analyse pas les classes dynamiques).
  const labelSpanClass = onDelete
    ? (hasRange ? 'sm:col-span-4' : 'sm:col-span-5')
    : (hasRange ? 'sm:col-span-5' : 'sm:col-span-6');

  return (
    <div
      className="grid grid-cols-12 gap-2 items-end py-1.5 border-b border-slate-100 last:border-b-0"
      data-testid={`${testidPrefix}-row`}
    >
      <div className={`col-span-12 ${labelSpanClass}`}>
        {editableLabel ? (
          <>
            <Label className="text-[10px] text-slate-400 uppercase">Libellé</Label>
            <div className="flex gap-1.5">
              {editableKey && (
                <Input
                  type="text"
                  value={item.key || ''}
                  onChange={(e) => onChange({ ...item, key: e.target.value.trim().toLowerCase().replace(/\s+/g, '_') })}
                  className="h-8 text-xs font-mono w-28"
                  placeholder="clé"
                  data-testid={`${testidPrefix}-key`}
                />
              )}
              <Input
                type="text"
                value={item.label || ''}
                onChange={(e) => onChange({ ...item, label: e.target.value })}
                className="h-8 text-sm flex-1"
                placeholder="Libellé"
                data-testid={`${testidPrefix}-label`}
              />
            </div>
          </>
        ) : (
          <Label className="text-xs text-slate-600">{item.label || item.key}</Label>
        )}
      </div>

      {hasRange && rangeFields.map((f) => (
        <div key={f} className="col-span-6 sm:col-span-1">
          <Label className="text-[10px] text-slate-400 uppercase">
            {f === 'min_years' ? 'Min a.' : f === 'max_years' ? 'Max a.' : f === 'min_value' ? 'Min' : f === 'max_value' ? 'Max' : f}
          </Label>
          <Input
            type="number"
            step="1"
            value={item[f] ?? ''}
            placeholder="∞"
            onChange={(e) => {
              const raw = e.target.value;
              onChange({ ...item, [f]: raw === '' ? null : Number(raw) });
            }}
            className="h-8 text-xs font-mono"
            data-testid={`${testidPrefix}-${f}`}
          />
        </div>
      ))}

      {fields.map((field) => (
        <div key={field} className="col-span-6 sm:col-span-2">
          <Label className="text-[10px] text-slate-400 uppercase">{field === 'coef' ? 'Coef' : field === 'pct_modulateur' ? '% modul.' : field}</Label>
          <Input type="number" step="0.01" min="0" value={item[field] ?? 0}
            onChange={(e) => onChange({ ...item, [field]: Number(e.target.value) || 0 })}
            className="h-8 text-sm font-mono"
            data-testid={`${testidPrefix}-${field}`}
          />
        </div>
      ))}

      {extra && !onDelete && <div className="col-span-12 sm:col-span-3 text-right">{extra}</div>}
      {onDelete && (
        <div className="col-span-12 sm:col-span-1 flex items-end justify-end">
          <button
            type="button"
            onClick={onDelete}
            className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors"
            title="Supprimer cette ligne"
            aria-label={`Supprimer ${item.label || item.key || 'ligne'}`}
            data-testid={`${testidPrefix}-delete`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Ligne d'édition d'un score-tier (label fixe + champ "Points" entier signé).
 * Sert pour : score_anciennete_tiers, score_experience_dirigeant_tiers, score_effectif_tiers.
 */
export function ScoreTierRow({ item, onChange, testidPrefix, editable = false, onDelete = null }) {
  const pts = Number(item.points) || 0;
  // Couleurs renforcées (contrast +) — fond opaque, bordure marquée, texte foncé pour bonne lisibilité
  const ptsClass = pts > 0 ? 'text-rose-800 bg-rose-100 border-rose-400 shadow-sm'
    : pts < 0 ? 'text-emerald-800 bg-emerald-100 border-emerald-400 shadow-sm'
    : 'text-slate-700 bg-slate-100 border-slate-300';
  return (
    <div
      className="grid grid-cols-12 gap-2 items-end py-1.5 border-b border-slate-100 last:border-b-0"
      data-testid={`${testidPrefix}-row`}
    >
      <div className="col-span-12 sm:col-span-4">
        {editable ? (
          <>
            <Label className="text-[10px] text-slate-400 uppercase">Libellé</Label>
            <Input
              type="text"
              value={item.label || ''}
              onChange={(e) => onChange({ ...item, label: e.target.value })}
              className="h-8 text-sm"
              placeholder="Libellé du palier"
              data-testid={`${testidPrefix}-label`}
            />
          </>
        ) : (
          <>
            <Label className="text-xs text-slate-600">{item.label}</Label>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {item.min_value ?? 0} → {item.max_value ?? '∞'}
            </div>
          </>
        )}
      </div>

      {editable && (
        <>
          <div className="col-span-3 sm:col-span-2">
            <Label className="text-[10px] text-slate-400 uppercase">Min</Label>
            <Input
              type="number"
              step="0.5"
              value={item.min_value ?? ''}
              placeholder="0"
              onChange={(e) => onChange({ ...item, min_value: e.target.value === '' ? null : Number(e.target.value) })}
              className="h-8 text-xs font-mono"
              data-testid={`${testidPrefix}-min`}
            />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <Label className="text-[10px] text-slate-400 uppercase">Max</Label>
            <Input
              type="number"
              step="0.5"
              value={item.max_value ?? ''}
              placeholder="∞"
              onChange={(e) => onChange({ ...item, max_value: e.target.value === '' ? null : Number(e.target.value) })}
              className="h-8 text-xs font-mono"
              data-testid={`${testidPrefix}-max`}
            />
          </div>
        </>
      )}

      <div className={`col-span-6 ${editable ? 'sm:col-span-2' : 'sm:col-span-3'}`}>
        <Label className="text-[10px] text-slate-400 uppercase">Points</Label>
        <Input type="number" step="1" value={item.points ?? 0}
          onChange={(e) => onChange({ ...item, points: parseInt(e.target.value, 10) || 0 })}
          className="h-8 text-sm font-mono"
          data-testid={`${testidPrefix}-points`}
        />
      </div>
      <div className={`col-span-6 ${editable ? 'sm:col-span-1' : 'sm:col-span-4'} text-right flex items-end justify-end gap-1`}>
        <Badge variant="outline" className={`font-mono text-xs font-semibold border-2 ${ptsClass}`}>
          {pts > 0 ? '+' : ''}{pts} pts
        </Badge>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors"
            title="Supprimer ce palier"
            aria-label={`Supprimer ${item.label || 'palier'}`}
            data-testid={`${testidPrefix}-delete`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
